/* eslint-disable @typescript-eslint/no-unused-vars */
import { CommonModule, KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  effect,
  EventEmitter,
  HostBinding,
  inject,
  input,
  Input,
  OnInit,
  Output,
  signal,
} from '@angular/core';

import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { DevicesManagerService } from '../../../../services/devices-manager.service';
import { LoadingService } from '../../../../services/loading.service';
import { RoutingService } from '../../../../services/routing.service';
import UnitsService from '../../../../services/units.service';

import { FormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '@ngx-translate/core';
import { DEVICES_DONUT } from '../../../../component/devices/devices-routing.constants';
import {
  PARK_STATUSES,
  ParkStatusName,
  ParkStatusState,
} from '../../../../component/devices/park-status.constants';
import { DevicesStatsService } from '../../../../services/devices-stats.service';
import { DonutComponent, DonutValue } from '../../library/donut/donut.component';
import { StatusCardComponent } from '../../library/status-card/status-card.component';

@Component({
  selector: 'park-kpi',
  imports: [MatSelectModule, FormsModule, CommonModule, TranslatePipe, DonutComponent, StatusCardComponent],
  templateUrl: './park-kpi.component.html',
  styleUrl: './park-kpi.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ParkKpiComponent implements OnInit {
  private _deviceStatsService = inject(DevicesStatsService);
  private _unitsService = inject(UnitsService);
  private _devicesManagerService = inject(DevicesManagerService);
  public routingService = inject(RoutingService);
  public translate = inject(TranslateService);
  private loadingService = inject(LoadingService);
  private router = inject(Router);

  @Output() updateTimeSelected: EventEmitter<number> = new EventEmitter<number>();
  /** Émet le `state` (clé de `STATUS_KEY`) de la carte cliquée, pour appliquer un filtre côté appelant. */
  @Output() statusClick: EventEmitter<string> = new EventEmitter<string>();
  @Input() isEdit: boolean;
  /** `state` actuellement filtré par l'appelant (voir `STATUS_KEY`), pour surligner la carte active. */
  @Input() selectedStatus: string | null = null;
  public dataGraph = this._deviceStatsService.$selectUnitWithStats;
  public time = this._deviceStatsService.selectTimeUsed;
  private dataLoaded = signal<boolean>(false);
  public donutValues = signal<DonutValue[]>([{ value: 0, color: 'var(--background-secondary)' }]);
  public usagePercentage: number;
  public degree = 0;

  public displayInline = input<boolean>(false);

  @HostBinding('style.flexDirection') get direction() {
    return this.displayInline() ? 'row' : 'column';
  }

  public statusNumber: Record<ParkStatusName, number | null> = {
    MOVE: null,
    STOP: null,
    FULLY_STOPPED: null,
    INACTIVE: null,
  };
  public statusPercentage: Record<ParkStatusName, number | null> = {
    MOVE: null,
    STOP: null,
    FULLY_STOPPED: null,
    INACTIVE: null,
  };

  public STATUS_COLOR: Record<ParkStatusName, string> = {
    MOVE: 'secondary',
    STOP: 'warning',
    FULLY_STOPPED: 'error',
    INACTIVE: 'primary',
  };

  /** Index in `statusArray`, taken from the shared park status table. */
  public STATUS_KEY = Object.fromEntries(
    PARK_STATUSES.map(status => [status.name, status.state])
  ) as Record<ParkStatusName, ParkStatusState>;

  public optionsTime = {
    0: this.translate.instant('DATE.TODAY'),
    1: this.translate.instant('DATE.YESTERDAY'),
    6: this.translate.instant('DATE.TIME.FROM_7_DAYS'),
    13: this.translate.instant('DATE.TIME.FROM_14_DAYS'),
    29: this.translate.instant('DATE.TIME.FROM_30_DAYS'),
    59: this.translate.instant('DATE.TIME.FROM_60_DAYS'),
    89: this.translate.instant('DATE.TIME.FROM_90_DAYS'),
  };

  constructor() {
    effect(() => {
      const dataGraph = this.dataGraph();
      const time = this.time();
      if (this.dataLoaded() && !!dataGraph && time !== null) {
        this.commonOperations();
      }
    });
  }

  ngOnInit(): void {
    // $serviceTree, not $unitsById: the KPIs are aggregated per service by DevicesStatsService,
    // which reads $serviceTree exclusively. $unitsById comes straight from the units liveQuery and
    // turns truthy before legacyProcessServices has built that tree.
    if (this._unitsService.$serviceTree() && this._devicesManagerService.$devicesMap()) {
      this.dataLoaded.set(true);
    } else {
      // servicesLoadedEvent$, not allLoadedEvent$: the latter only fires once all eleven load
      // events have, and several of them are conditional (news() is skipped when its idLoop flag
      // survives, maintenances only run with the option). servicesLoadedEvent$ fires right after
      // legacyProcessServices/legacyProcessDevices — exactly what this widget reads.
      this.loadingService.servicesLoadedEvent$.pipe(take(1)).subscribe(() => {
        this.dataLoaded.set(true);
      });
    }
  }

  public clickDonut() {
    this.router.navigate([DEVICES_DONUT]);
  }

  public updateCount(daysBeforeSelected: number) {
    this.time.set(daysBeforeSelected);
    this.updateTimeSelected.emit(daysBeforeSelected);
  }

  public commonOperations() {
    this.usagePercentage = this._deviceStatsService.getDonutPercent(this.dataGraph(), this.time()) || 0;
    this.donutValues.set([{ value: this.usagePercentage, color: 'var(--background-secondary)' }]);
    this._sanitizeKPIStatus();
  }

  // It's to parse the horrible statusArray object and keep clean the template
  private _sanitizeKPIStatus() {
    const dataGraph = this.dataGraph();
    this.statusNumber.MOVE = dataGraph.statusArray[3].length;
    this.statusNumber.STOP = dataGraph.statusArray[2].length;
    this.statusNumber.FULLY_STOPPED = dataGraph.statusArray[1].length;
    this.statusNumber.INACTIVE = dataGraph.statusArray[0].length;

    // Add fallback values since NaN occurs when a service has no vehicles
    this.statusPercentage.MOVE = dataGraph.statusArray.perc3 || 0;
    this.statusPercentage.STOP = dataGraph.statusArray.perc2 || 0;
    this.statusPercentage.FULLY_STOPPED = dataGraph.statusArray.perc1 || 0;
    this.statusPercentage.INACTIVE = dataGraph.statusArray.perc0 || 0;
  }

  // Need to check if it's a number because when a service has no vehicle I get NaN
  public ensureValueIsNumber(value) {
    if (isNaN(value)) {
      return 0;
    } else {
      return value;
    }
  }

  // This function preserves the original order of object keys when iterating in template loops
  public originalOrder = (
    a: KeyValue<string, (typeof this.optionsTime)[keyof typeof this.optionsTime]>,
    b: KeyValue<string, (typeof this.optionsTime)[keyof typeof this.optionsTime]>
  ): number => {
    return 0;
  };
}
